import { useState, useEffect, useMemo } from 'react'
import { format, addMinutes, addHours, addDays, startOfTomorrow, setHours, setMinutes } from 'date-fns'
import { Button, Modal, DateTimePicker } from '@/components/ui'

type PresetId = '30min' | '1hr' | '4hr' | '12hr' | 'tomorrow9' | 'custom'

const PRESETS: { id: PresetId; label: string }[] = [
  { id: '30min', label: 'In 30 min' },
  { id: '1hr', label: 'In 1 hour' },
  { id: '4hr', label: 'In 4 hours' },
  { id: '12hr', label: 'In 12 hours' },
  { id: 'tomorrow9', label: 'Tomorrow 9 AM' },
  { id: 'custom', label: 'Custom' },
]

function resolvePreset(id: PresetId): Date | null {
  const now = new Date()
  switch (id) {
    case '30min':
      return addMinutes(now, 30)
    case '1hr':
      return addHours(now, 1)
    case '4hr':
      return addHours(now, 4)
    case '12hr':
      return addHours(now, 12)
    case 'tomorrow9':
      return setMinutes(setHours(startOfTomorrow(), 9), 0)
    case 'custom':
      return null
  }
}

interface ScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (scheduledFor: string) => void
  isLoading: boolean
  title?: string
}

export function ScheduleModal({ isOpen, onClose, onConfirm, isLoading, title = 'Schedule Post' }: ScheduleModalProps) {
  const [selectedPreset, setSelectedPreset] = useState<PresetId | null>(null)
  const [customDateTime, setCustomDateTime] = useState('')

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPreset(null)
      setCustomDateTime('')
    }
  }, [isOpen])

  const now = new Date()
  const maxDate = addDays(now, 30)

  const resolvedDate = useMemo(() => {
    if (!selectedPreset) return null
    if (selectedPreset === 'custom') {
      if (!customDateTime) return null
      return new Date(customDateTime)
    }
    return resolvePreset(selectedPreset)
  }, [selectedPreset, customDateTime])

  const isValid = resolvedDate !== null && resolvedDate > now && resolvedDate <= maxDate

  const handleConfirm = () => {
    if (!isValid || !resolvedDate) return
    onConfirm(resolvedDate.toISOString())
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={isLoading} disabled={!isValid || isLoading}>
            Confirm
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => {
            const isSelected = selectedPreset === preset.id
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => setSelectedPreset(preset.id)}
                className={`
                  inline-flex items-center px-3 py-1.5 text-sm font-medium cursor-pointer
                  border rounded-full transition-colors
                  ${isSelected
                    ? 'bg-primary-text text-primary-bg border-primary-text'
                    : 'bg-transparent text-secondary-text border-border hover:border-primary-text hover:text-primary-text'
                  }
                `}
              >
                {preset.label}
              </button>
            )
          })}
        </div>

        {selectedPreset === 'custom' && (
          <DateTimePicker
            label="Date & Time"
            value={customDateTime}
            onChange={(e) => setCustomDateTime(e.target.value)}
            minDate={now}
            max={format(maxDate, "yyyy-MM-dd'T'HH:mm")}
          />
        )}

        {resolvedDate && isValid && (
          <p className="text-sm text-secondary-text">
            {format(resolvedDate, 'EEE, MMM d, yyyy h:mm a')}
          </p>
        )}

        {resolvedDate && !isValid && (
          <p className="text-sm text-error">
            {resolvedDate <= now
              ? 'Scheduled time must be in the future.'
              : 'Scheduled time cannot be more than 30 days from now.'}
          </p>
        )}
      </div>
    </Modal>
  )
}
